// src/context/AuthProvider.tsx
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

type User = {
  id: string;
  email: string;
  displayName: string;
  roles: {
    id: number;
    type: number;
  }[];
};

type AuthContextValue = {
  user: User | null;
};

export const fetchMe = async (): Promise<User> => {
  const { data } = await api.get<User>("/users/current");
  return data;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const hasAccount = accounts.length > 0;
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  const { data, isError } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: false,
    enabled: hasAccount, // 👈 wait until MSAL has an account
  });

  useEffect(() => {
    if (data) setUser(data);
    if (isError) {
      setUser(null);
      queryClient.removeQueries({ queryKey: ["me"] });
    }
  }, [data, isError, queryClient]);


  const value = useMemo(
    () => ({
      user,
      setUser,
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
